import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, X, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { WIDGET_REGISTRY } from "@/lib/widget-registry";

interface WidgetShellProps {
  widgetId: string;
  widgetType: string;
  title?: string | null;
  size: 'full' | 'half' | 'quarter';
  children: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  isDragging?: boolean;
}

export function WidgetShell({ 
  widgetId, 
  widgetType, 
  title, 
  size, 
  children, 
  onEdit, 
  onDelete, 
  isDragging 
}: WidgetShellProps) {
  const config = WIDGET_REGISTRY[widgetType];
  const displayTitle = title || config?.defaultTitle || 'Widget';
  
  return (
    <div 
      className={cn(
        "widget-container h-full",
        isDragging && "opacity-50"
      )}
      data-testid={`widget-${widgetId}`}
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">{displayTitle}</CardTitle>
          <div className="flex items-center gap-2">
            {onEdit && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onEdit} 
                data-testid={`button-edit-widget-${widgetId}`}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onDelete} 
                data-testid={`button-delete-widget-${widgetId}`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <div 
              className="cursor-grab active:cursor-grabbing" 
              data-testid={`drag-handle-${widgetId}`}
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
